const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TAG_PATTERN = /^[a-z]{3}$/;
const TRID_PATTERN = /^([a-z]{3})_([A-Za-z0-9_-]{22})$/;

export type TridConversionErrorCode =
  | "invalid_uuid"
  | "invalid_tag"
  | "invalid_trid"
  | "invalid_trid_payload";

export class TridConversionError extends Error {
  readonly code: TridConversionErrorCode;

  constructor(code: TridConversionErrorCode) {
    super(code);
    this.name = "TridConversionError";
    this.code = code;
  }
}

const bytesToBase64Url = (bytes: Uint8Array): string => {
  const binary = String.fromCharCode(...bytes);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const base64UrlToBytes = (value: string): Uint8Array => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");

  try {
    const binary = atob(padded);
    return Uint8Array.from(binary, (character) => character.charCodeAt(0));
  } catch {
    throw new TridConversionError("invalid_trid_payload");
  }
};

export const uuidToTrid = (uuid: string, tag: string): string => {
  const normalizedUuid = uuid.trim();
  const normalizedTag = tag.trim().toLowerCase();

  if (!UUID_PATTERN.test(normalizedUuid)) {
    throw new TridConversionError("invalid_uuid");
  }

  if (!TAG_PATTERN.test(normalizedTag)) {
    throw new TridConversionError("invalid_tag");
  }

  const hex = normalizedUuid.replace(/-/g, "");
  const bytes = Uint8Array.from(hex.match(/.{2}/g) ?? [], (byte) => Number.parseInt(byte, 16));

  return `${normalizedTag}_${bytesToBase64Url(bytes)}`;
};

export interface ParsedTrid {
  tag: string;
  uuid: string;
}

export const tridToUuid = (trid: string): ParsedTrid => {
  const match = TRID_PATTERN.exec(trid.trim());

  if (!match) {
    throw new TridConversionError("invalid_trid");
  }

  const [, tag, payload] = match;
  const bytes = base64UrlToBytes(payload);

  if (bytes.length !== 16 || bytesToBase64Url(bytes) !== payload) {
    throw new TridConversionError("invalid_trid_payload");
  }

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  const uuid = [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");

  return { tag, uuid };
};
